import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getServerURL } from '../services/api';
import { Watch, MapPin, Building, Mail } from 'lucide-react';
import ImageCarousel from '../components/ImageCarousel';

const PublicWatchPage = () => {
  const { id } = useParams();
  const [watch, setWatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWatch();
  }, [id]);

  const fetchWatch = async () => {
    try {
      const response = await fetch(`${getServerURL()}/api/watches/public/${id}`);
      if (!response.ok) {
        throw new Error('Watch not found or not available for public viewing');
      }
      const data = await response.json();
      setWatch(data);
    } catch (error) {
      console.error('Error fetching watch:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-stone-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !watch) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-stone-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Watch className="w-10 h-10 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Watch Not Available</h2>
          <p className="text-gray-600 mb-4">
            This watch is not currently available for public viewing.
          </p>
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 text-left">
            <p className="text-sm text-gray-700 font-semibold mb-2">For Watch Owners:</p>
            <p className="text-xs text-gray-600">
              To share this watch publicly, make sure it's marked as "Public Listing" in your inventory.
              Toggle the switch in your watch details page to enable public sharing.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-stone-100 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-900 rounded-full font-semibold mb-4">
            <Watch className="w-5 h-5" />
            Luxury Timepiece
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
            {watch.brand} {watch.model}
          </h1>
          {watch.reference && (
            <p className="text-lg text-gray-600">Reference: {watch.reference}</p>
          )}
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Images */}
            <div className="p-8 bg-gray-50">
              <ImageCarousel
                images={watch.images || []}
                alt={`${watch.brand} ${watch.model}`}
              />
            </div>

            {/* Details */}
            <div className="p-8">
              {/* Price */}
              <div className="mb-8">
                <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">Price</p>
                <p className="text-4xl font-bold text-gray-900">{formatPrice(watch.asking_price)}</p>
                <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-semibold ${
                  watch.status === 'Available' ? 'bg-green-100 text-green-800' :
                  watch.status === 'Reserved' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {watch.status}
                </span>
              </div>

              {/* Specifications */}
              <div className="space-y-4 mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Specifications</h3>

                <div className="grid grid-cols-2 gap-4">
                  {watch.year && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Year</p>
                      <p className="text-sm font-semibold text-gray-900">{watch.year}</p>
                    </div>
                  )}
                  {watch.condition && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Condition</p>
                      <p className="text-sm font-semibold text-gray-900">{watch.condition}</p>
                    </div>
                  )}
                  {watch.case_size && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Case Size</p>
                      <p className="text-sm font-semibold text-gray-900">{watch.case_size}</p>
                    </div>
                  )}
                  {watch.case_material && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Case Material</p>
                      <p className="text-sm font-semibold text-gray-900">{watch.case_material}</p>
                    </div>
                  )}
                  {watch.movement && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Movement</p>
                      <p className="text-sm font-semibold text-gray-900">{watch.movement}</p>
                    </div>
                  )}
                  {watch.bracelet_material && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Bracelet</p>
                      <p className="text-sm font-semibold text-gray-900">{watch.bracelet_material}</p>
                    </div>
                  )}
                </div>

                {/* What's Included */}
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Included</p>
                  <div className="space-y-1 text-sm text-gray-700">
                    {watch.box && <p>✓ Original Box</p>}
                    {watch.papers && <p>✓ Original Papers</p>}
                    {watch.warranty && <p>✓ Warranty Card</p>}
                    {watch.service_papers && <p>✓ Service Documentation</p>}
                    {!watch.box && !watch.papers && !watch.warranty && !watch.service_papers && (
                      <p className="text-gray-500">Watch only</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              {watch.description && (
                <div className="mb-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-700 whitespace-pre-line">{watch.description}</p>
                </div>
              )}

              {/* Dealer Info */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Listed By</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-gray-900">
                    <Building className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold">{watch.dealer.dealer_name}</span>
                    {watch.dealer.company_name && (
                      <span className="text-gray-500">• {watch.dealer.company_name}</span>
                    )}
                  </div>
                  {(watch.dealer.city || watch.dealer.state || watch.dealer.country) && (
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <span>
                        {watch.dealer.city}{watch.dealer.city && watch.dealer.state ? ', ' : ''}
                        {watch.dealer.state} {watch.dealer.country}
                      </span>
                    </div>
                  )}
                </div>

                {/* Contact CTA */}
                <div className="mt-6">
                  <a
                    href={`mailto:?subject=Inquiry about ${watch.brand} ${watch.model}&body=I'm interested in this watch: ${window.location.href}`}
                    className="block w-full text-center px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all"
                  >
                    <Mail className="w-5 h-5 inline mr-2" />
                    Contact Dealer
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>Powered by <span className="font-semibold text-gray-900">Dial-Grid</span></p>
        </div>
      </div>
    </div>
  );
};

export default PublicWatchPage;
